import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./styles/FilesFoldersBlock.module.scss";
import { observer } from "mobx-react";
import BlockTitle from "../_BlockTitle";
import FilesList from "./FilesList";
import { getContentItemFileViewerType } from "../../SchoolBlocks/SharedFilesBlock/SharedFilesBlock";
import { FileViewer } from "../../SchoolBlocks/SharedFilesBlock/NormalizedFileBlock";
import { CSSTransition } from "react-transition-group";
import classNames from "classnames";
import { ITerraceProps } from "../Terrace";
import ApiClient from "../../../../backends/ApiClient";
import NotificationManager from "../../../notifications/NotificationManager";
import { runInAction } from "mobx";
import { getIconSet } from "./fileUtilities";
import { EditDropDownItem } from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";

import {
  IReactiveFileContentItem,
  IReactiveFolderContentItem,
  ReactiveContentItem,
} from "../../../schoolfeed/SchoolFeedContentItem";
import { useStores } from "../../../../stores/useStores";
import { Box, Button, Select } from "@radix-ui/themes";
import useTheme from "../../../radix/useTheme";

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

interface ModifiedFolderContentItem extends IReactiveFolderContentItem {
  children: Array<IReactiveFileContentItem | ModifiedFolderContentItem>;
  hasFetchedChildren: boolean;
  nextPageToken: null | string;
}

type IFilesFoldersListAndPreviewProps = {
  expandedView: boolean;
  filesAndFolders: Array<IReactiveFileContentItem | IReactiveFolderContentItem>;
  aboveTheFold?: boolean;
  handleFetchMore?: () => void;
  shouldFetchMore?: boolean;
  loadingMore: boolean;
  useInfiniteScroll?: boolean;
  handleDelete: (contentItem: ContentItem) => void;
  blockObj: IFilesTerraceBlock;
  sortBy?: string;
  ref?: RefObject<HTMLDivElement | null>;
} & (
  | {
      editing: true;
      setSortBy: (value: string) => void;
      handleCancel: () => void;
      handleSave: () => void;
    }
  | {
      editing: false;
      setSortBy?: (value: string) => void;
      handleCancel?: never;
      handleSave?: never;
    }
) &
  (
    | {
        handleSave: () => void;
        handleCancel: () => void;
      }
    | {
        handleSave?: undefined;
        handleCancel?: undefined;
      }
  );

type PreviewableItem = (FileContentItem | FolderContentItem) & {
  isExternalGoogleResult?: boolean;
};

export function canPreviewContentItem(
  item: PreviewableItem | null | undefined,
  titleOrg: OrganizationTypeOrganization,
) {
  if (!item) return false;
  if (item.isExternalGoogleResult) return false;
  if (!item.is_file) return false;

  const fileItem = item as FileContentItem;
  const fileOrigin = getIconSet(fileItem.type);
  const provider = titleOrg.json_data.settings?.identity?.provider;
  return (
    (fileOrigin === "google" || fileOrigin === provider) &&
    (fileItem.json_data.file.embedded_view_link ||
      !fileItem.type.includes("o365"))
  );
}

export const FilesFoldersListAndPreview = observer(
  (props: IFilesFoldersListAndPreviewProps) => {
    const { interfaceStore, organizationStore } = useStores();
    const radixTheme = useTheme();
    const { aboveTheFold = true } = props;
    const [currentPath, setCurrentPath] = useState<string>("");
    const [loadingFolderContents, setLoadingFolderContents] = useState(false);
    const [selectedContentItem, setSelectedContentItem] = useState<
      FileContentItem | undefined
    >(
      props.filesAndFolders[0]?.is_file
        ? (props.filesAndFolders[0] as FileContentItem)
        : undefined,
    );
    const [previewIsExpanded, setPreviewIsExpanded] = useState(false);
    const hasValidViewLink = canPreviewContentItem(
      selectedContentItem,
      organizationStore.organization,
    );

    const filesFoldersContainerClassName = classNames({
      [styles.filesFoldersContainer]: true,
      [styles.filesFoldersContainerSelected]:
        selectedContentItem && props.aboveTheFold,
    });

    const selectedContainerClassName = classNames({
      [styles.contentContainer]: true,
      [styles.contentContainerSelected]: hasValidViewLink,
    });

    const filePreviewClassName = classNames({
      [styles.filePreview]: true,
      [styles.filePreviewSelected]: hasValidViewLink,
      [styles.filePreviewExpanded]: previewIsExpanded,
    });

    const isMobile = interfaceStore.breakpoint !== "break-point-lg";

    const filesToDisplay = useMemo(() => {
      if (!currentPath) return props.filesAndFolders;
      let f = props.filesAndFolders;
      currentPath.split(".").forEach((pathSegment) => {
        const next = f.find((f) => f.third_party_id === pathSegment);
        if (next) f = (next as ModifiedFolderContentItem).children || [];
      });
      return f;
    }, [currentPath, loadingFolderContents, props.filesAndFolders]);

    const currentSubFolder = useMemo<ModifiedFolderContentItem | null>(() => {
      if (!currentPath) return null;
      let f = props.filesAndFolders;
      let p: null | ModifiedFolderContentItem = null;
      currentPath.split(".").forEach((pathSegment) => {
        const found = f.find((f) => f.third_party_id === pathSegment);
        if (found) {
          p = found as ModifiedFolderContentItem;
          f = (found as ModifiedFolderContentItem).children;
        }
      });
      return p;
    }, [currentPath, props.filesAndFolders]);

    const upwardsNavigationButtonText = currentPath
      ? currentSubFolder
        ? currentSubFolder.title
        : "Top Level"
      : undefined;

    async function fetchFolderContents(
      folder: ModifiedFolderContentItem,
      nextPageToken: string | null = null,
    ) {
      const client = await ApiClient();
      // the spec types this response as a Terrace, but at runtime it's a
      // custom { items, nextPageToken } envelope (SBSB-2912)
      const parsed = await (client.GET(
        "/organizations/{organization_pk}/terraces/nested_folder_contents/",
        {
          params: {
            path: {
              organization_pk: organizationStore.currentOrganization.id,
            },
            query: {
              folder_url: folder.json_data.folder.view_link,
              sort_by: props.sortBy,
              page_token: nextPageToken ?? undefined,
            },
          },
        },
      ) as any);
      folder.update({
        children: [
          ...(folder.children || []),
          ...parsed.items.map(
            (item) =>
              new ReactiveContentItem({
                ...item,
                published: true, // these aren't real content items so don't have `published` field
                children: [],
                hasFetchedChildren: !item.is_folder,
              }),
          ),
        ],
        hasFetchedChildren: true,
        nextPageToken: parsed.nextPageToken,
      });
    }

    async function handleFetchMore() {
      if (currentSubFolder) {
        setLoadingFolderContents(true);
        try {
          await fetchFolderContents(
            currentSubFolder,
            currentSubFolder.nextPageToken,
          );
        } catch {
          NotificationManager.error("Something went wrong!");
        }
        setLoadingFolderContents(false);
      } else if (props.handleFetchMore) {
        return props.handleFetchMore();
      }
    }

    return (
      <div className={filesFoldersContainerClassName} ref={props.ref}>
        <div className={selectedContainerClassName}>
          <FilesList
            files_folders={filesToDisplay}
            setSelectedContentItem={setSelectedContentItem}
            selectedItem={selectedContentItem}
            setPreviewIsExpanded={setPreviewIsExpanded}
            previewIsExpanded={previewIsExpanded}
            aboveTheFold={aboveTheFold}
            handleFetchMore={handleFetchMore}
            shouldFetchMore={
              currentSubFolder
                ? !!currentSubFolder?.nextPageToken
                : props.shouldFetchMore
            }
            maxHeight={props.expandedView ? "initial" : undefined}
            useInfiniteScroll={props.useInfiniteScroll}
            handleDelete={props.handleDelete}
            handleFolderClick={async (folder: ModifiedFolderContentItem) => {
              setCurrentPath((curr) =>
                curr
                  ? curr + "." + folder.third_party_id
                  : folder.third_party_id,
              );
              if (!folder.hasFetchedChildren) {
                setLoadingFolderContents(true);
                try {
                  await fetchFolderContents(folder);
                } catch {
                  NotificationManager.error("Something went wrong!");
                  setCurrentPath((curr) =>
                    curr.split(".").slice(0, -1).join("."),
                  );
                }
                setLoadingFolderContents(false);
              }
            }}
            upwardsNavigationButtonText={upwardsNavigationButtonText}
            handleUpwardsNavigation={() => {
              setCurrentPath((curr) => curr.split(".").slice(0, -1).join("."));
            }}
            loading={props.loadingMore || loadingFolderContents}
          />
          {props.editing && props.blockObj && (
            <>
              <div className={styles.sortByContainer}>
                <div className={styles.inputGroup}>
                  <label htmlFor="settings_sortBy">Sort by:</label>
                  <div>
                    <Select.Root
                      value={props.sortBy}
                      onValueChange={(value) => props.setSortBy(value)}
                    >
                      <Select.Trigger id="settings_sortBy" />
                      <Select.Content
                        position={"popper"}
                        container={radixTheme.portalContainer?.current}
                      >
                        <Select.Item value={"title"}>
                          Filename (A-Z)
                        </Select.Item>
                        <Select.Item value={"-title"}>
                          Filename (Z-A)
                        </Select.Item>
                        <Select.Item value={"-updated_at"}>
                          Last Modified Date (Newest-Oldest)
                        </Select.Item>
                        <Select.Item value={"updated_at"}>
                          Last Modified Date (Oldest-Newest)
                        </Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </div>
                </div>
              </div>
              {props.handleSave && (
                <Box pt={"2"}>
                  <Box pr={"2"} display={"inline-block"}>
                    <Button onClick={props.handleSave}>Save and Exit</Button>
                  </Box>
                  <Box display={"inline-block"}>
                    <Button variant={"outline"} onClick={props.handleCancel}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </div>
        {!isMobile && (
          <div className={filePreviewClassName}>
            {hasValidViewLink && selectedContentItem && (
              <FileViewer
                viewerType={getContentItemFileViewerType(selectedContentItem)}
                viewLink={selectedContentItem.json_data.file.view_link}
                embeddedViewLink={
                  selectedContentItem?.json_data.file.embedded_view_link
                }
                thirdPartyId={selectedContentItem.third_party_id}
                uniqueId={selectedContentItem.id}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

interface FilesFoldersBlockProps extends ITerraceProps {
  blockObj: Omit<IFilesTerraceBlock, "content_items"> & {
    content_items: Array<IReactiveFileContentItem | IReactiveFolderContentItem>;
  };
}

const FilesFoldersBlock = observer((props: FilesFoldersBlockProps) => {
  const { gridStore, sidebarStore } = useStores();
  const transitionRef = useRef<HTMLDivElement>(null);

  const [editing, setEditing] = useState(false);
  const [sortBy, setSortBy] = useState(
    props.blockObj.json_data?.files?.sort_by
      ? props.blockObj.json_data?.files.sort_by
      : "-updated_at",
  );
  const [contentItems, setContentItems] = useState(
    props.blockObj.content_items,
  );
  const [page, setPage] = useState(2);
  const [loading, setLoading] = useState(false);
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState(
    props.blockObj.is_more,
  );
  const saveSortBy = useCallback(async () => {
    try {
      const client = await ApiClient();
      const updatedTerrace = await (client.PATCH(
        "/organizations/{organization_pk}/terraces/{id}/",
        {
          params: {
            path: {
              organization_pk: props.blockObj.organization_id,
              id: props.blockObj.id,
            },
          },
          body: {
            json_data: {
              files: {
                sort_by: sortBy,
              },
            },
          } as never,
        },
      ) as any);
      NotificationManager.success("Sort By Updated!");
      runInAction(() => {
        gridStore.blocks.set(props.blockObj.id, updatedTerrace);
      });
      setContentItems(updatedTerrace.content_items);
      setEditing(!editing);
    } catch {
      setSortBy(props.blockObj.json_data.files.sort_by);
      NotificationManager.error("Something went wrong! Please try again.");
    }
  }, [sortBy]);

  function handleCancel() {
    setSortBy(props.blockObj.json_data.files.sort_by);
    setEditing(false);
  }

  const handleFetchMore = async () => {
    setLoading(true);
    const client = await ApiClient();
    const json_result = await client.GET(
      "/organizations/{organization_pk}/content-items/",
      {
        params: {
          path: { organization_pk: props.blockObj.organization_id },
          query: {
            is_folder: true,
            is_file: true,
            page_size: 12,
            page: page,
            expand: "organization",
            ordering: sortBy,
          },
        },
      },
    );
    const newData = json_result.data as unknown as Array<
      FileContentItem | FolderContentItem
    >;
    const next_link = json_result.links.next;
    if (next_link === null) {
      setShouldShowMoreButton(false);
    }
    setContentItems((prevContentItems) => [
      ...prevContentItems,
      ...newData.map(
        (d) =>
          new ReactiveContentItem(d) as
            | IReactiveFolderContentItem
            | IReactiveFileContentItem,
      ),
    ]);
    setPage(page + 1);
    setLoading(false);
    return newData;
  };

  const onClick = () => {
    sidebarStore.setSidebar({
      view: "AddSocial",
      sbtype: "GOOGLE_DRIVE",
    });
  };

  useEffect(() => {
    // Need to run a useEffect to update file published status / styling in Terrace view
    setContentItems(props.blockObj.content_items);
  }, [props.blockObj.content_items]);

  const filesFoldersBlockDropDownItems: EditDropDownItem[] = [
    {
      title: "Add File",
      icon: <FontAwesome prefix={"fas"} fixedWidth className="fa-plus" />,
      onClick: onClick,
    },
    {
      title: "Sort By",
      icon: <FontAwesome prefix={"fas"} fixedWidth className="fa-pencil-alt" />,
      onClick: () => setEditing(true),
    },
    {
      title: "Manage Files/Folders",
      icon: <FontAwesome prefix={"fas"} fixedWidth className="fa-pencil-alt" />,
      onClick: () => sidebarStore.setSidebar({ view: "Social" }),
    },
  ];

  return (
    <section>
      <BlockTitle
        blockObj={props.blockObj}
        aboveTheFold={props.aboveTheFold}
        dropDownItems={filesFoldersBlockDropDownItems}
        allowSave={true}
        showViewMore={false}
      />
      <div className={styles.transitionWrapper}>
        <CSSTransition
          nodeRef={transitionRef}
          appear
          classNames={transitionClassNames}
          in={props.aboveTheFold}
          timeout={500}
        >
          <div
            className={styles.filesFoldersContainerTerrace}
            ref={transitionRef}
          >
            <FilesFoldersListAndPreview
              ref={transitionRef}
              expandedView={false}
              handleFetchMore={handleFetchMore}
              shouldFetchMore={shouldShowMoreButton}
              loadingMore={loading}
              filesAndFolders={contentItems}
              aboveTheFold={props.aboveTheFold}
              handleDelete={props.handleDeleteContentItem}
              blockObj={props.blockObj}
              sortBy={sortBy}
              {...(editing
                ? {
                    editing: true as const,
                    handleSave: saveSortBy,
                    handleCancel,
                    setSortBy,
                  }
                : { editing: false as const })}
            />
          </div>
        </CSSTransition>
      </div>
    </section>
  );
});

export default FilesFoldersBlock;
